import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { authFetch } from '../../utils/auth-fetch';
import { AlertTriangle, BellRing, ChevronDown, ChevronRight } from 'lucide-react';
import { Absent } from './opsConsoleAbsent';

/**
 * Ops Console — Alerts (PRD vault offering/2026-09-25-shopify-ops-alert-email-prd §5b).
 *
 * ONE SOURCE: the rows the Shopify ops-alert e-mails are built from (table shopify_ops_alerts, read through
 * GET /ops-console/:platform/alerts). The "what to do" cell is the §4 verdict label and the same admin link the
 * e-mail carries — nothing is re-classified here.
 *
 * OBSERVE-ONLY (the console's launch policy): no acknowledge / resolve action. The first action must bring the
 * audit layer with it.
 *
 * Absent-not-blank: "no alerts in this window" (a measured empty list) and "the alert table is unavailable" (the
 * backend could not read it — 503) are different states and render differently. A failed request is an error, never
 * an empty list.
 */

export type AlertSeverity = 'ACTION' | 'BUG' | 'INFO';

/**
 * One failure's delivery state, derived server-side from the row's fingerprints (review #6582 r9): owed to an
 * immediate e-mail, pending the daily summary, delivered by an e-mail, included in a summary. Several can hold.
 */
interface FailureDelivery {
  token: string;
  owed: boolean;
  pending_summary: boolean;
  delivered: boolean;
  summarized: boolean;
}

interface AlertRowPayload {
  product_feed_item_id: string | null;
  reference?: string | null;
  delivery?: FailureDelivery;
  name: string | null;
  handle: string | null;
  category: string | null;
  category_source: string | null;
  shopify_spt_id: string | null;
  shopify_matched_spt_id: string | null;
  status: string | null;
  error_message: string | null;
}

interface AlertClassPayload {
  id: string;
  row: number | null;
  severity: AlertSeverity;
  verdict: string;
  title: string;
  what_to_do: string;
  link: string | null;
  count: number;
  rows_omitted: number;
  rows: AlertRowPayload[];
  delivery?: FailureDelivery;
}

export interface OpsAlert {
  id: string;
  kind: string;
  severity: AlertSeverity;
  shop: string | null;
  connection_id: string | null;
  site_id: string | null;
  batch_job_id: string | null;
  summary: string | null;
  verdict_label: string | null;
  product_count: number | null;
  first_seen: string;
  last_seen: string;
  count: number;
  sent_at: string | null;
  send_failures?: number;
  last_send_error?: string | null;
  pending_since?: string | null;
  info_pending_since?: string | null;
  info_pending_count?: number;
  summarized_at?: string | null;
  summary_failures?: number;
  last_summary_error?: string | null;
  /** Another source than Shopify (rev. 5.2 generic store): its source, and — a GROUPED alert — what is outstanding now. */
  source?: string;
  outstanding_count?: number | null;
  resolved_at?: string | null;
  /**
   * The EVIDENCE of record (review #6582 r9): every failure ever recorded under this alert's key, each with its delivery
   * state — not only the latest occurrence, which can hide an earlier failure that is still owed.
   */
  evidence: {
    what_to_do?: { verdict: string; text: string; link: string | null } | null;
    classes?: AlertClassPayload[];
    details?: Record<string, unknown> | null;
  } | null;
}

interface AlertsResponse {
  alerts: OpsAlert[];
  next_cursor: string | null;
  window: { since: string; until: string };
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready' }
  | { kind: 'unavailable'; message: string }
  | { kind: 'error'; message: string };

// The alert kinds the hooks write (services/pipeline/shopifyOpsAlert.js). A kind not in this list still renders; the
// list only feeds the filter.
export const ALERT_KINDS = [
  'batch_anomaly', 'batch_hold', 'batch_merchant', 'batch_fatal',
  'install_import_failed', 'import_products_excluded', 'activation_needs_person',
  'webhook_category_unreadable', 'category_resync_exhausted', 'search_datastore_missing', 'billing_inconsistent',
  'merchant_email_failed',
];
// #7222: enriched-feed delivery (webhook) alerts — the generic store's `delivery` source, listed by the console's
// "delivery" adapter (vault PRD 2026-10-09-tradeinn-webhook-delivery rev. 5.2, "Alerts": the seven kinds), plus the
// PR #7228 follow-up's delivery_encryption_key_unavailable (the sending host cannot decrypt stored credentials).
export const DELIVERY_ALERT_KINDS = [
  'delivery_circuit_paused', 'delivery_auth_paused', 'delivery_endpoint_redirecting', 'delivery_product_held',
  'delivery_render_multiple_rows', 'delivery_run_failed', 'delivery_schema_load_failed', 'delivery_encryption_key_unavailable',
];
const ALL_SEVERITIES: AlertSeverity[] = ['ACTION', 'BUG', 'INFO'];
// §5b: the default view is ACTION + BUG — INFO is one click away.
const DEFAULT_SEVERITIES: AlertSeverity[] = ['ACTION', 'BUG'];

const SEVERITY_CLASS: Record<AlertSeverity, string> = {
  ACTION: 'bg-red-100 text-red-800 border-red-200',
  BUG: 'bg-amber-100 text-amber-900 border-amber-200',
  INFO: 'bg-slate-100 text-slate-700 border-slate-200',
};

const fmtDateTime = (v: string | null) => (v ? new Date(v).toLocaleString() : null);
const isoDay = (d: Date) => d.toISOString().slice(0, 10);

export function SeverityPill({ severity }: { severity: AlertSeverity }) {
  return (
    <span data-testid="alert-severity" className={`inline-block rounded border px-1.5 py-0.5 text-xs font-semibold ${SEVERITY_CLASS[severity] ?? ''}`}>
      {severity}
    </span>
  );
}

/** The "what to do" cell: the §4 verdict label and the admin surface link, exactly as the e-mail carries them. */
export function WhatToDo({ alert }: { alert: OpsAlert }) {
  const w = alert.evidence?.what_to_do ?? null;
  const verdict = alert.verdict_label ?? w?.verdict ?? null;
  if (!verdict) return <Absent why="The alert carries no verdict" />;
  return (
    <div className="text-xs space-y-0.5" data-testid="alert-what-to-do">
      <div className="font-medium">{verdict}</div>
      {w?.link ? (
        <a href={w.link} target="_blank" rel="noreferrer" className="text-blue-600 underline break-all">{w.link}</a>
      ) : null}
    </div>
  );
}

// The retry pass stops at this many failed sends (services/pipeline/shopifyOpsAlert.js MAX_SEND_FAILURES).
const MAX_SEND_FAILURES = 5;

/** Whether the e-mail went out — INFO rows go in the daily summary, so "not sent" is not a failure for them. */
/**
 * The SUMMARY path, independent of the row's severity and of any earlier delivery (review #6582 r6): INFO occurrences
 * owed to the next daily summary — a recurring INFO row summarized once is pending again; an ACTION row can owe the
 * summary a later INFO occurrence too.
 */
function SummaryState({ alert }: { alert: OpsAlert }) {
  const last = alert.summarized_at ?? (alert.severity === 'INFO' ? alert.sent_at : null);
  const pendingCount = alert.info_pending_count ?? 0;
  const failures = alert.summary_failures ?? 0;
  let pending: React.ReactNode = null;
  if (alert.info_pending_since) {
    const what = `${pendingCount} occurrence${pendingCount === 1 ? '' : 's'}`;
    pending = failures > 0
      ? <span className="text-amber-700" data-testid="alert-summary-failing">{what} owed to the daily summary — the last {failures} summary send{failures === 1 ? '' : 's'} failed — {alert.last_summary_error}</span>
      : <span className="text-amber-700" data-testid="alert-summary-pending">{what} pending the next daily summary (since {fmtDateTime(alert.info_pending_since)})</span>;
  }
  if (!last && !pending) return alert.severity === 'INFO' ? <span className="text-muted-foreground">not in a summary yet</span> : null;
  return (
    <span className="space-x-2">
      {last && <span className="text-muted-foreground" data-testid="alert-summarized">in the summary of {fmtDateTime(last)}</span>}
      {pending}
    </span>
  );
}

export function DeliveryState({ alert }: { alert: OpsAlert }) {
  const failures = alert.send_failures ?? 0;
  if (alert.severity === 'INFO') {
    return <SummaryState alert={alert} />;
  }
  // The last CONFIRMED delivery and an OWED one (first send or follow-up) are separate facts; both are shown.
  const owedWhat = alert.sent_at ? 'follow-up' : 'e-mail';
  let owed: React.ReactNode = null;
  if (alert.pending_since) {
    if (failures >= MAX_SEND_FAILURES) {
      owed = <span className="text-red-600" data-testid="alert-not-delivered">{owedWhat} not delivered after {failures} attempts — {alert.last_send_error}</span>;
    } else if (failures > 0) {
      owed = <span className="text-amber-700" data-testid="alert-delivery-retrying">{owedWhat} not delivered yet ({failures} failed attempt{failures === 1 ? '' : 's'}, retrying) — {alert.last_send_error}</span>;
    } else {
      owed = <span className="text-amber-700" data-testid="alert-delivery-pending">{owedWhat} pending since {fmtDateTime(alert.pending_since)}</span>;
    }
  }
  return (
    <span className="space-x-2">
      {alert.sent_at
        ? <span className="text-muted-foreground" data-testid="alert-delivered">e-mailed {fmtDateTime(alert.sent_at)}</span>
        : (!owed && <span className="text-muted-foreground">not e-mailed</span>)}
      {owed}
      <SummaryState alert={alert} />
    </span>
  );
}

/** Short marker for the time cell: an owed delivery that has already failed. */
function deliveryMarker(a: OpsAlert): string | null {
  if (a.info_pending_since && (a.summary_failures ?? 0) > 0) return 'summary delivery failing';
  const failures = a.send_failures ?? 0;
  if (a.severity === 'INFO' || !a.pending_since || failures === 0) return null;
  const what = a.sent_at ? 'follow-up' : 'e-mail';
  return failures >= MAX_SEND_FAILURES ? `${what} not delivered` : `${what} delivery retrying`;
}

/** evidence.details — the exception message and identifiers of alerts that carry no product rows (fatal, import, activation, data store, billing). */
function AlertFacts({ details }: { details: Record<string, unknown> | null | undefined }) {
  const entries = Object.entries(details ?? {}).filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (entries.length === 0) return null;
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5 text-xs" data-testid="alert-details">
      {entries.map(([k, v]) => (
        <React.Fragment key={k}>
          <dt className="text-muted-foreground">{k}</dt>
          <dd className="whitespace-pre-wrap break-words">{typeof v === 'string' ? v : JSON.stringify(v)}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

/**
 * One failure's delivery state, in words. "Not delivered" when an e-mail owes it and the retry pass gave up.
 */
function FailureState({ delivery, alert }: { delivery: FailureDelivery | undefined; alert: OpsAlert }) {
  if (!delivery) return <Absent why="No delivery state recorded" />;
  const parts: React.ReactNode[] = [];
  const failures = alert.send_failures ?? 0;
  if (delivery.owed) {
    parts.push(failures >= MAX_SEND_FAILURES
      ? <span key="owed" className="text-red-600" data-testid="failure-not-delivered">not delivered ({failures} failed attempts)</span>
      : <span key="owed" className="text-amber-700" data-testid="failure-owed">e-mail owed{failures > 0 ? ` (${failures} failed attempt${failures === 1 ? '' : 's'})` : ''}</span>);
  }
  if (delivery.pending_summary) parts.push(<span key="ps" className="text-amber-700" data-testid="failure-pending-summary">pending the daily summary</span>);
  if (delivery.delivered) parts.push(<span key="d" className="text-muted-foreground" data-testid="failure-delivered">e-mailed{alert.sent_at ? ` (last e-mail ${fmtDateTime(alert.sent_at)})` : ''}</span>);
  if (delivery.summarized) parts.push(<span key="s" className="text-muted-foreground" data-testid="failure-summarized">in a daily summary{alert.summarized_at ? ` (last ${fmtDateTime(alert.summarized_at)})` : ''}</span>);
  if (parts.length === 0) parts.push(<span key="r" className="text-muted-foreground" data-testid="failure-recorded">recorded only</span>);
  return <span className="space-x-2" data-testid="failure-state">{parts}</span>;
}

function AlertDetail({ alert }: { alert: OpsAlert }) {
  const classes = alert.evidence?.classes ?? [];
  return (
    <div className="space-y-3" data-testid="alert-detail">
      {alert.batch_job_id && <div className="text-xs text-muted-foreground">Batch job {alert.batch_job_id}</div>}
      <div className="text-xs">Delivery: <DeliveryState alert={alert} /></div>
      <AlertFacts details={alert.evidence?.details} />
      {classes.length === 0 && <div className="text-xs text-muted-foreground">This alert carries no per-product rows.</div>}
      {classes.map((c) => (
        <div key={c.id} className="space-y-1">
          <div className="text-sm font-medium flex items-center gap-2">
            <SeverityPill severity={c.severity} /> {c.title} — {c.count}
            {c.row ? <span className="text-xs text-muted-foreground">(PRD §4 row {c.row})</span> : null}
          </div>
          <div className="text-xs"><b>Verdict:</b> {c.verdict}</div>
          <div className="text-xs"><b>What to do:</b> {c.what_to_do}{c.link ? <> — <a href={c.link} target="_blank" rel="noreferrer" className="text-blue-600 underline break-all">{c.link}</a></> : null}</div>
          {c.rows.length === 0 && c.delivery && <div className="text-xs"><b>Delivery:</b> <FailureState delivery={c.delivery} alert={alert} /></div>}
          {c.rows.length > 0 && (
            <table className="w-full text-xs border-collapse" data-testid="alert-product-rows">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="pr-2">Product</th><th className="pr-2">Category</th><th className="pr-2">category_source</th><th className="pr-2">Status</th><th className="pr-2">error_message</th><th>Delivery</th>
                </tr>
              </thead>
              <tbody>
                {c.rows.map((r, i) => (
                  <tr key={`${r.product_feed_item_id ?? r.name ?? 'row'}-${i}`} className="align-top border-t">
                    <td className="pr-2">
                      {r.name ?? <Absent why="No product name recorded" />}
                      {r.handle ? <div className="text-muted-foreground">{r.handle}</div> : null}
                      {r.product_feed_item_id ? <div className="text-muted-foreground break-all">{r.product_feed_item_id}</div> : null}
                    </td>
                    <td className="pr-2">{r.category ?? <Absent why="No category recorded" />}</td>
                    <td className="pr-2">{r.category_source ?? <Absent why="No category_source recorded for this product" />}</td>
                    <td className="pr-2">{r.status ?? <Absent why="No status recorded" />}</td>
                    <td className="pr-2 whitespace-pre-wrap break-words">{r.error_message ?? <Absent why="No error message recorded" />}</td>
                    <td className="whitespace-nowrap"><FailureState delivery={r.delivery} alert={alert} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {c.rows_omitted > 0 && <div className="text-xs text-muted-foreground">… {c.rows_omitted} more not listed (the count is exact)</div>}
        </div>
      ))}
    </div>
  );
}

export function AlertsTable({ alerts, showShop = true, initiallyExpanded = [], shopLabel = 'Shop' }: { alerts: OpsAlert[]; showShop?: boolean; initiallyExpanded?: string[]; shopLabel?: string }) {
  // initiallyExpanded: rows open on first render — the Batches panel opens the one alert it was asked for (PRD 2026-10-08 §6.4).
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(initiallyExpanded));
  const toggle = (id: string) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  return (
    <Table data-testid="alerts-table">
      <TableHeader>
        <TableRow>
          <TableHead />
          <TableHead>Time</TableHead>
          <TableHead>Severity</TableHead>
          <TableHead>Kind</TableHead>
          {showShop && <TableHead>{shopLabel}</TableHead>}
          <TableHead>Summary</TableHead>
          <TableHead>Products</TableHead>
          <TableHead>What to do</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {alerts.map((a) => {
          const open = expanded.has(a.id);
          return (
            <React.Fragment key={a.id}>
              <TableRow data-testid="alert-row" data-alert-id={a.id} className="cursor-pointer" onClick={() => toggle(a.id)}>
                <TableCell>{open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}</TableCell>
                <TableCell className="whitespace-nowrap text-xs">
                  {fmtDateTime(a.last_seen)}
                  {a.count > 1 && <div className="text-muted-foreground">{a.count}× since {fmtDateTime(a.first_seen)}</div>}
                  {deliveryMarker(a) && <div className="text-red-600" data-testid="alert-delivery-marker">{deliveryMarker(a)}</div>}
                </TableCell>
                <TableCell><SeverityPill severity={a.severity} /></TableCell>
                <TableCell className="text-xs">{a.kind}</TableCell>
                {showShop && <TableCell className="text-xs">{a.shop ?? <Absent why="The alert names no shop" />}</TableCell>}
                <TableCell className="text-xs">{a.summary ?? <Absent why="The alert carries no summary" />}</TableCell>
                <TableCell className="text-xs">
                  {a.outstanding_count !== undefined && a.outstanding_count !== null ? (
                    // a GROUPED alert (held products): what is still held now, and whether it resolved
                    <span data-testid="alert-outstanding">
                      {a.resolved_at ? `Resolved ${fmtDateTime(a.resolved_at)}` : `${a.outstanding_count} still held`}
                    </span>
                  ) : a.product_count === null ? <Absent why="No product count recorded" /> : a.product_count}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}><WhatToDo alert={a} /></TableCell>
              </TableRow>
              {open && (
                <TableRow data-testid="alert-expanded" data-alert-id={a.id}>
                  <TableCell />
                  <TableCell colSpan={showShop ? 7 : 6}><AlertDetail alert={a} /></TableCell>
                </TableRow>
              )}
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}

async function fetchAlerts(path: string, signal: AbortSignal): Promise<{ state: LoadState; data: AlertsResponse | null }> {
  const res = await authFetch(`/ops-console/${path}`, { signal });
  if (res.status === 503) {
    const body = await res.json().catch(() => ({}));
    if (body.code === 'alerts_unavailable') return { state: { kind: 'unavailable', message: body.message || 'Alert table unavailable' }, data: null };
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || body.error || `HTTP ${res.status}`);
  }
  const data = await res.json();
  // ⛔ No silent fallback: a payload without the list is an error, never "no alerts".
  if (!Array.isArray(data?.alerts)) throw new Error('Alerts payload is missing alerts[] — the admin build and the backend are probably out of step.');
  return { state: { kind: 'ready' }, data };
}

const isAbort = (err: unknown) =>
  err instanceof DOMException ? err.name === 'AbortError' : (err as { name?: string })?.name === 'AbortError';

function StateLine({ state, empty }: { state: LoadState; empty: boolean }) {
  if (state.kind === 'loading') return <p className="text-sm text-muted-foreground">Loading alerts…</p>;
  if (state.kind === 'unavailable') {
    return (
      <p className="text-sm text-amber-800 flex items-center gap-1" data-testid="alerts-unavailable">
        <AlertTriangle className="h-4 w-4" /> Alert table unavailable — {state.message} This is not "no alerts".
      </p>
    );
  }
  if (state.kind === 'error') {
    return (
      <p className="text-sm text-red-600 flex items-center gap-1" data-testid="alerts-error">
        <AlertTriangle className="h-4 w-4" /> Alerts failed to load: {state.message}
      </p>
    );
  }
  if (empty) return <p className="text-sm text-muted-foreground" data-testid="alerts-empty">No alerts in this window.</p>;
  return null;
}

/**
 * The fleet Alerts panel (§5b): filters severity / kind / shop / date range; default ACTION + BUG, last 7 days.
 * With `connectionId` it is the drill-down's "Alerts (last 30 days)" for that merchant (same row component).
 */
const SHOPIFY_DESCRIPTION = 'The same alerts the Shopify ops e-mails carry, newest first. INFO alerts are not e-mailed one by one — they go out in the daily summary and are listed here. Read-only.';

export default function OpsAlertsPanel({
  platform, connectionId = null, title = 'Alerts', description = SHOPIFY_DESCRIPTION, kinds = ALERT_KINDS,
  shopLabel = 'Shop', shopPlaceholder = 'shop.myshopify.com', testId = 'alerts-panel',
}: {
  platform: string; connectionId?: string | null; title?: string; description?: string; kinds?: string[];
  shopLabel?: string; shopPlaceholder?: string; testId?: string;
}) {
  const now = new Date();
  const [severities, setSeverities] = useState<AlertSeverity[]>(connectionId ? ALL_SEVERITIES : DEFAULT_SEVERITIES);
  const [kind, setKind] = useState<string>('');
  const [shop, setShop] = useState<string>('');
  const [sinceDay, setSinceDay] = useState<string>(isoDay(new Date(now.getTime() - (connectionId ? 30 : 7) * 86400000)));
  const [untilDay, setUntilDay] = useState<string>('');
  const [alerts, setAlerts] = useState<OpsAlert[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const abortRef = useRef<AbortController | null>(null);

  const buildPath = useCallback((nextCursor: string | null) => {
    const qs = new URLSearchParams();
    if (severities.length > 0 && severities.length < ALL_SEVERITIES.length) qs.set('severity', severities.join(','));
    if (kind) qs.set('kind', kind);
    if (shop.trim() && !connectionId) qs.set('shop', shop.trim());
    if (sinceDay) qs.set('since', `${sinceDay}T00:00:00.000Z`);
    if (untilDay) qs.set('until', `${untilDay}T23:59:59.999Z`);
    if (nextCursor) qs.set('cursor', nextCursor);
    const base = connectionId ? `${platform}/merchants/${encodeURIComponent(connectionId)}/alerts` : `${platform}/alerts`;
    const q = qs.toString();
    return q ? `${base}?${q}` : base;
  }, [platform, connectionId, severities, kind, shop, sinceDay, untilDay]);

  const load = useCallback(async (append: boolean, nextCursor: string | null) => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    if (!append) setState({ kind: 'loading' });
    try {
      const { state: s, data } = await fetchAlerts(buildPath(nextCursor), ctrl.signal);
      if (ctrl.signal.aborted) return;
      setState(s);
      if (data) {
        setAlerts((prev) => (append ? [...prev, ...data.alerts] : data.alerts));
        setCursor(data.next_cursor);
      } else {
        setAlerts([]);
        setCursor(null);
      }
    } catch (err) {
      if (isAbort(err)) return;
      setState({ kind: 'error', message: err instanceof Error ? err.message : String(err) });
      if (!append) setAlerts([]);
    }
  }, [buildPath]);

  useEffect(() => {
    load(false, null);
  }, [load]);

  useEffect(() => () => abortRef.current?.abort(), []);

  // At least ONE severity stays selected (review #6582 r7): with none, the request would carry no severity filter and
  // the backend would answer ALL severities while every button reads "off" — the control would lie.
  const toggleSeverity = (s: AlertSeverity) => setSeverities((prev) => {
    if (!prev.includes(s)) return [...prev, s];
    return prev.length === 1 ? prev : prev.filter((x) => x !== s);
  });

  return (
    <Card data-testid={connectionId ? 'merchant-alerts' : testId}>
      <CardHeader className="pb-2">
        <CardTitle className={`${connectionId ? 'text-sm' : 'text-base'} flex items-center gap-2`}>
          <BellRing className="h-4 w-4" />{connectionId ? 'Alerts (last 30 days)' : title}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-end gap-3 text-xs" data-testid="alerts-filters">
          <div className="flex items-center gap-1" role="group" aria-label="Severity">
            {ALL_SEVERITIES.map((s) => (
              <Button key={s} size="sm" variant={severities.includes(s) ? 'default' : 'outline'}
                aria-pressed={severities.includes(s)} data-testid={`alerts-severity-${s}`}
                title={severities.length === 1 && severities.includes(s) ? 'At least one severity stays selected' : undefined}
                onClick={() => toggleSeverity(s)}>
                {s}
              </Button>
            ))}
          </div>
          <label className="flex flex-col gap-0.5">Kind
            <select className="border rounded px-1 py-1" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="alerts-kind">
              <option value="">All kinds</option>
              {kinds.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </label>
          {!connectionId && (
            <label className="flex flex-col gap-0.5">{shopLabel}
              <input className="border rounded px-1 py-1" placeholder={shopPlaceholder} value={shop}
                onChange={(e) => setShop(e.target.value)} data-testid="alerts-shop" />
            </label>
          )}
          <label className="flex flex-col gap-0.5">From
            <input type="date" className="border rounded px-1 py-1" value={sinceDay} onChange={(e) => setSinceDay(e.target.value)} data-testid="alerts-since" />
          </label>
          <label className="flex flex-col gap-0.5">To
            <input type="date" className="border rounded px-1 py-1" value={untilDay} onChange={(e) => setUntilDay(e.target.value)} data-testid="alerts-until" />
          </label>
        </div>
        <StateLine state={state} empty={alerts.length === 0} />
        {state.kind === 'ready' && alerts.length > 0 && <AlertsTable alerts={alerts} showShop={!connectionId} shopLabel={shopLabel} />}
        {state.kind === 'ready' && cursor && (
          <Button size="sm" variant="outline" onClick={() => load(true, cursor)} data-testid="alerts-more">Load more</Button>
        )}
      </CardContent>
    </Card>
  );
}
